// src/Pages/Hmo/ProductSelection.js
// ✅ FIXED: Uses Benefits field from backend for description & features
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';  // ✅ Your auth fetch utility

function ProductSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);  // ✅ Start with loading
  const [error, setError] = useState(null);
  
  // ✅ State for tracking params including agent attribution
  const [companyId, setCompanyId] = useState(null);
  const [linkId, setLinkId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  
  // ✅ State for products fetched from backend
  const [products, setProducts] = useState([]);

  // ✅ FSL Brand Color - ORANGE
  const PRIMARY_COLOR = '#F89423';
  const PRIMARY_LIGHT = '#FFF8F0';
  const PRIMARY_BORDER = '#FDE4C8';
  const PRIMARY_HOVER = '#E0851F';

  const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

  // ✅ AGGRESSIVE: Extract agentId from ALL possible sources
  const extractAgentId = () => {
    // Priority 1: URL param
    const fromUrl = searchParams.get('agent');
    if (fromUrl && fromUrl.trim() !== '') {
      console.log('✅ Agent from URL:', fromUrl);
      return fromUrl.trim();
    }
    
    // Priority 2: localStorage with multiple keys
    try {
      const storageKeys = ['agentSession', 'userSession', 'hmoSession'];
      for (const key of storageKeys) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const session = JSON.parse(raw);
          const candidates = [
            session.agentId,
            session.agent_id, 
            session.sessionId,
            session.session_id
          ];
          const found = candidates.find(id => id && id.trim() !== '');
          if (found) {
            console.log(`✅ Agent from localStorage[${key}]:`, found);
            return found.trim();
          }
        }
      }
    } catch (err) {
      console.warn('⚠️ Failed to parse localStorage:', err);
    }
    
    // Priority 3: Referrer URL
    try {
      const referrer = new URL(document.referrer);
      const fromReferrer = referrer.searchParams.get('agent');
      if (fromReferrer && fromReferrer.trim() !== '') {
        console.log('✅ Agent from referrer:', fromReferrer);
        return fromReferrer.trim();
      }
    } catch (err) {
      // Ignore referrer errors
    }
    
    console.warn('❌ AgentId NOT FOUND in any source');
    return null;
  };

  // ✅ HELPER: Parse benefits string into features array
  const parseBenefits = (benefits) => {
    if (!benefits) return [];
    
    // If already an array, return it
    if (Array.isArray(benefits)) return benefits;
    
    // If it's a string, try to parse as JSON first
    if (typeof benefits === 'string') {
      try {
        const parsed = JSON.parse(benefits);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // Not JSON - split by common delimiters
        return benefits
          .split(/[\n,;•\-\*]+/)  // Split by newline, comma, semicolon, bullet, dash, asterisk
          .map(item => item.trim())
          .filter(item => item.length > 0);
      }
    }
    
    return [];
  };

  // ✅ Fetch products from backend endpoint on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // ✅ Extract params
        const company = searchParams.get('company');
        const link = searchParams.get('link');
        const agent = extractAgentId();
        
        if (!company || !link) {
          throw new Error('Missing required parameters: company or link');
        }
        
        setCompanyId(company);
        setLinkId(link);
        setAgentId(agent);
        
        console.log('📍 Fetching products for:', { company, link, agent });
        
        // ✅ Call backend endpoint: POST /Settings/links/company/{companyId}
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/links/company/${company}?userType=hmo`,
          'POST',  // ✅ Your endpoint uses POST
          {}  // Empty body - params in URL
        );
        
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to fetch links');
        }
        
        // ✅ Find the specific link by linkId
        const links = response.data || [];
        const targetLink = links.find(l => l.linkId === link);
        
        if (!targetLink) {
          throw new Error(`Link not found: ${link}`);
        }
        
        // ✅ Extract bundled products
        const bundledProducts = targetLink.bundledProducts || [];
        
        if (bundledProducts.length === 0) {
          toast.warning('No products available for this link');
          setProducts([]);
          return;
        }
        
        // ✅ Format products for UI display with Benefits integration
        const formattedProducts = bundledProducts.map((p, index) => {
          // ✅ Parse benefits from backend (handles string or array)
          const benefitsArray = parseBenefits(p.benefits || p.Benefits);
          
          // ✅ Use Benefits for description if available, otherwise fallback
          const description = p.benefits || p.Benefits 
            ? (typeof (p.benefits || p.Benefits) === 'string' 
                ? `Comprehensive health coverage: ${p.benefits || p.Benefits}`
                : 'Comprehensive health coverage')
            : `Comprehensive ${p.categoryName || p.CategoryName || 'health'} coverage`;
          
          return {
            id: p.id || p.Id,
            category: p.categoryName || p.CategoryName || 'Health',
            name: p.name || p.Name || `Product ${index + 1}`,
            provider: p.providerName || p.ProviderName || 'Bastion Health Limited',
            price: p.minimumPremium ?? p.MinimumPremium ?? 35000,
            period: 'Month',
            description: description,
            
            // ✅ Use parsed benefits as features list
            features: benefitsArray.length > 0 
              ? benefitsArray 
              : [
                  'Basic outpatient care',
                  'Emergency services',
                  'General consultations',
                  'Basic laboratory tests',
                  'Prescription medications'
                ],
            
            icon: 'heart',
            popular: index === 0,  // ✅ Mark first as popular
            
            // ✅ Store original product data for reference
            _original: p
          };
        });
        
        console.log('✅ Loaded products:', formattedProducts.length);
        setProducts(formattedProducts);
        
      } catch (err) {
        console.error('💥 Error fetching products:', err);
        setError(err.message || 'Failed to load products');
        toast.error(err.message || 'Could not load products');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [searchParams]);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const handleContinue = () => {
    if (!selectedProduct) {
      toast.warning('Please select a product to continue');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      // ✅ Build query string preserving ALL params including agent
      const queryParams = new URLSearchParams();
      if (companyId) queryParams.set('company', companyId);
      if (linkId) queryParams.set('link', linkId);
      if (selectedProduct) queryParams.set('product', selectedProduct.id);
      
      // ✅ CRITICAL: Preserve agent attribution
      if (agentId) {
        queryParams.set('agent', agentId);
        console.log('✅ Preserving agent attribution:', agentId);
      } else {
        console.warn('⚠️ No agentId to preserve - attribution will be lost');
        // Last resort fallback
        const lastResort = extractAgentId();
        if (lastResort) {
          queryParams.set('agent', lastResort);
          console.log('✅ Last-resort agent attribution:', lastResort);
        }
      }
      
      const queryString = queryParams.toString();
      console.log('🔗 Navigating with params:', queryString);
      
      // ✅ Navigate to Product Details page with full attribution
      const redirectPath = queryString ? `/insurance/product-details?${queryString}` : '/insurance/product-details';
      
      navigate(redirectPath);
    }, 800);
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  const formatPrice = (price) => {
    return `₦${Number(price).toLocaleString()}`;
  };

  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  // ✅ Loading State
  if (loading && products.length === 0) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem', color: PRIMARY_COLOR }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading products...</p>
        </div>
      </div>
    );
  }

  // ✅ Error State
  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center p-4" style={{ maxWidth: '400px' }}>
          <div className="rounded-circle bg-danger bg-opacity-10 text-danger d-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px', fontSize: '24px' }}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h5 className="fw-semibold mb-2">Error Loading Products</h5>
          <p className="text-muted mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn"
            style={{ backgroundColor: PRIMARY_COLOR, color: 'white', border: 'none', padding: '8px 24px', borderRadius: '8px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ✅ Empty State (no products)
  if (products.length === 0) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center p-4" style={{ maxWidth: '400px' }}>
          <div className="rounded-circle bg-light text-muted d-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px', fontSize: '24px' }}>
            <i className="fas fa-box-open"></i>
          </div>
          <h5 className="fw-semibold mb-2">No Products Available</h5>
          <p className="text-muted mb-4">This link doesn't have any products configured yet.</p>
          <button 
            onClick={handleClose}
            className="btn btn-outline-secondary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            {/* ✅ FSL Logo */}
            <div className="d-flex align-items-center">
              <img 
                src="/images/fsl_logo.png"
                alt="FSL Logo" 
                style={{ height: '40px' }} 
              />
            </div>
            
            {/* Close Button */}
            <button 
              className="btn btn-light rounded-circle"
              style={{ width: '36px', height: '36px' }}
              onClick={handleClose}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 py-5">
        <div className="container">
          
          {/* Page Title */}
          <div className="text-center mb-5">
            <h1 className="fw-bold" style={{ fontSize: '28px', color: '#111827' }}>
              Select Product
            </h1>
            {/* ✅ Agent Attribution Badge */}
            {agentId && (
              <div className="mt-2">
                <span 
                  className="badge"
                  style={{ 
                    backgroundColor: `${PRIMARY_COLOR}15`,
                    color: PRIMARY_COLOR,
                    border: `1px solid ${PRIMARY_BORDER}`,
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  <i className="fas fa-user-tag me-1"></i>
                  Agent: {agentId.substring(0, 8)}...
                </span>
              </div>
            )}
          </div>

          {/* ✅ Debug Info - Dev Only */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-center mb-4">
              <small className="text-muted">
                Company: {companyId} | Link: {linkId} | Agent: {agentId || 'NOT FOUND'} | Products: {products.length}
              </small>
            </div>
          )}

          <div className="row g-4">
            
            {/* Left Column - Product List */}
            <div className="col-lg-5 col-md-6">
              <div 
                className="rounded-3 p-4 h-100"
                style={{ backgroundColor: 'white' }}
              >
                {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
                  <div key={category} className="mb-4">
                    {/* Category Header */}
                    <div className="d-flex align-items-center gap-2 mb-3 pb-2" style={{ borderBottom: '2px solid #f3f4f6' }}>
                      <div 
                        className="rounded-2 p-2 d-flex align-items-center justify-content-center"
                        style={{ 
                          backgroundColor: PRIMARY_LIGHT,
                          color: PRIMARY_COLOR,
                          width: '40px',
                          height: '40px'
                        }}
                      >
                        <i className={`fas fa-${category === 'Health' ? 'heart' : 'briefcase'}`}></i>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-semibold" style={{ color: PRIMARY_COLOR }}>
                          {category}
                        </h6>
                        <small className="text-muted">
                          {categoryProducts.length} {categoryProducts.length === 1 ? 'product' : 'products'}
                        </small>
                      </div>
                    </div>

                    {/* Products List */}
                    <div className="d-flex flex-column gap-2">
                      {categoryProducts.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className="p-3 rounded-3 position-relative"
                          style={{
                            backgroundColor: selectedProduct?.id === product.id ? PRIMARY_LIGHT : 'white',
                            border: `2px solid ${selectedProduct?.id === product.id ? PRIMARY_COLOR : '#e5e7eb'}`,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.currentTarget.style.backgroundColor = selectedProduct?.id === product.id ? PRIMARY_LIGHT : 'white';
                            }
                          }}
                        >
                          {product.popular && (
                            <span 
                              className="badge position-absolute top-0 end-0 m-2"
                              style={{ 
                                backgroundColor: PRIMARY_COLOR,
                                fontSize: '10px',
                                padding: '4px 8px'
                              }}
                            >
                              POPULAR
                            </span>
                          )}
                          
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div 
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  backgroundColor: selectedProduct?.id === product.id ? PRIMARY_COLOR : '#f3f4f6',
                                  color: selectedProduct?.id === product.id ? 'white' : '#6b7280',
                                  width: '36px',
                                  height: '36px',
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}
                              >
                                {product.name.charAt(0)}
                              </div>
                              <div>
                                <h6 className="mb-0 fw-semibold" style={{ fontSize: '15px' }}>
                                  {product.name}
                                </h6>
                                <small className="text-muted" style={{ fontSize: '12px' }}>
                                  {product.provider}
                                </small>
                              </div>
                            </div>
                            <div className="text-end">
                              <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                Starting from:
                              </small>
                              <div 
                                className="fw-bold"
                                style={{ 
                                  color: selectedProduct?.id === product.id ? PRIMARY_COLOR : '#111827',
                                  fontSize: '14px'
                                }}
                              >
                                {formatPrice(product.price)}/{product.period}
                              </div>
                            </div>
                          </div>
                          
                          {selectedProduct?.id === product.id && (
                            <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${PRIMARY_BORDER}` }}>
                              <small className="text-muted" style={{ fontSize: '12px' }}>
                                ✓ Selected
                              </small>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Product Details */}
            <div className="col-lg-7 col-md-6">
              <div 
                className="rounded-3 p-4 h-100"
                style={{ 
                  backgroundColor: 'white',
                  minHeight: '500px'
                }}
              >
                {selectedProduct ? (
                  <div>
                    {/* Product Header */}
                    <div className="d-flex align-items-start justify-content-between mb-4 pb-3" style={{ borderBottom: '2px solid #f3f4f6' }}>
                      <div className="d-flex align-items-center gap-3">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: PRIMARY_COLOR,
                            color: 'white',
                            width: '48px',
                            height: '48px',
                            fontWeight: '700',
                            fontSize: '18px'
                          }}
                        >
                          {selectedProduct.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="fw-bold mb-1" style={{ fontSize: '22px' }}>
                            {selectedProduct.name}
                          </h3>
                          <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                            {selectedProduct.provider}
                          </p>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block" style={{ fontSize: '12px' }}>
                          Starting from
                        </small>
                        <h4 className="fw-bold mb-0" style={{ color: PRIMARY_COLOR, fontSize: '20px' }}>
                          {formatPrice(selectedProduct.price)}/{selectedProduct.period}
                        </h4>
                      </div>
                    </div>

                    {/* Description - Uses Benefits field */}
                    <div className="mb-4">
                      <p className="text-muted mb-0" style={{ lineHeight: '1.6' }}>
                        {selectedProduct.description}
                      </p>
                    </div>

                    {/* Features/What's Included - Parsed from Benefits */}
                    <div className="mb-4">
                      <h6 className="fw-semibold mb-3" style={{ fontSize: '16px' }}>
                        What's included:
                      </h6>
                      <div 
                        className="p-3 rounded-3"
                        style={{ 
                          backgroundColor: '#f9fafb',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}
                      >
                        <ul className="list-unstyled mb-0">
                          {selectedProduct.features.map((feature, index) => (
                            <li key={index} className="mb-2 d-flex align-items-start gap-2">
                              <i 
                                className="fas fa-check-circle mt-1"
                                style={{ color: PRIMARY_COLOR, fontSize: '14px', flexShrink: 0 }}
                              ></i>
                              <span className="text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Continue Button - ORANGE */}
                    <button
                      onClick={handleContinue}
                      disabled={loading}
                      className="btn w-100 py-3 fw-semibold rounded-3"
                      style={{
                        backgroundColor: PRIMARY_COLOR,
                        color: 'white',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = PRIMARY_HOVER;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = PRIMARY_COLOR;
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </button>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center p-5">
                    <div 
                      className="rounded-circle mb-4 d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: PRIMARY_LIGHT,
                        color: PRIMARY_COLOR,
                        width: '80px',
                        height: '80px',
                        fontSize: '32px'
                      }}
                    >
                      <i className="fas fa-hand-pointer"></i>
                    </div>
                    <h5 className="fw-semibold mb-2" style={{ color: '#111827' }}>
                      Select a Product
                    </h5>
                    <p className="text-muted mb-0" style={{ maxWidth: '300px' }}>
                      Choose from the available products on the left to view details and continue with your enrollment.
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3">
        <div className="container text-center">
          <p className="text-muted small mb-0">
            <i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i>
            Powered by{' '}
            <span style={{ fontWeight: '600', color: PRIMARY_COLOR }}>FSL</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ProductSelection;